import handleError from 'scaleflow/utils/typescript-helpers/handle-error';
import type { ITriggerWorkflow, IN8NClient } from './types';

class N8NClient {
  baseUrl = '';

  constructor({ baseUrl }: IN8NClient) {
    this.baseUrl = baseUrl;
  }

  async *createPrediction(
    config: ITriggerWorkflow,
    abortController?: AbortController,
  ) {
    const response = await fetch(`${config.url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config.payload),
      signal: abortController?.signal,
    });

    if (!response.body) {
      throw new Error('ReadableStream not supported');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        // Check if the request has been aborted
        if (abortController?.signal.aborted) {
          break;
        }

        const { done, value } = await reader.read();

        if (done) {
          // Yield any remaining buffer content
          if (buffer.trim()) {
            try {
              const finalItem = JSON.parse(buffer.trim()) as Record<
                string,
                string
              >;
              yield finalItem;
            } catch (e) {
              // Yield raw buffer if it can't be parsed
              yield { raw: buffer, parseError: handleError(e) };
            }
          }
          break;
        }

        // Decode the chunk immediately
        const decodedChunk = decoder.decode(value, { stream: true });
        buffer += decodedChunk;

        // Parse and yield complete JSON objects immediately
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIndex).trim();
          buffer = buffer.slice(newlineIndex + 1);

          if (line) {
            try {
              const parsedItem = JSON.parse(line) as Record<string, string>;
              yield parsedItem; // Yield immediately
            } catch (parseError: unknown) {
              // Yield raw line with error info
              yield { raw: line, parseError: handleError(parseError) };
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}

export default N8NClient;
